import type {
  ActivityLogResponse,
  OnboardingDashboard,
  OnboardingListQuery,
  OnboardingListResponse,
  ProcessFlowResponse,
  UpdateStatusRequest,
} from '@/types/onboarding';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Onboarding Service
 * Handles all onboarding-related API endpoints from /api/v1/onboarding/*
 */

export const onboardingService = {
  /**
   * Get dashboard metrics
   */
  async getDashboard(): Promise<OnboardingDashboard> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<OnboardingDashboard>('/api/v1/onboarding/dashboard', {}, token);
  },

  /**
   * Get paginated list of onboardings with filtering
   */
  async getList(query: OnboardingListQuery = {}): Promise<OnboardingListResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const searchParams = new URLSearchParams();
    if (query.status) searchParams.append('status', query.status);
    if (query.search) searchParams.append('search', query.search);
    if (query.limit) searchParams.append('limit', query.limit.toString());
    if (query.offset) searchParams.append('offset', query.offset.toString());

    const queryString = searchParams.toString();
    const url = `/api/v1/onboarding/list${queryString ? `?${queryString}` : ''}`;

    return authenticatedRequest<OnboardingListResponse>(url, {}, token);
  },

  /**
   * Get process flow with average completion times
   */
  async getProcessFlow(): Promise<ProcessFlowResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<ProcessFlowResponse>('/api/v1/onboarding/process-flow', {}, token);
  },

  /**
   * Update onboarding status for an employee
   */
  async updateStatus(userId: string, data: UpdateStatusRequest): Promise<{ message: string }> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<{ message: string }>(
      `/api/v1/onboarding/${userId}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Get activity log for a specific employee
   */
  async getActivityLog(userId: string): Promise<ActivityLogResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<ActivityLogResponse>(
      `/api/v1/onboarding/${userId}/activity`,
      {},
      token
    );
  },
};
